import { useState } from 'react';
import LoadingButton from '../LoadingButton';

interface AmountFormProps {
  initialAmount: number;
  isProcessing: boolean;
  onSubmit: (amount: number) => void;
  onCancel: () => void;
}

export default function AmountForm({
  initialAmount,
  isProcessing,
  onSubmit,
  onCancel
}: AmountFormProps) {
  const [amount, setAmount] = useState(initialAmount.toString());

  // Calculate the processing fee (5% of the amount) for display only
  const calculateProcessingFee = (value: string) => {
    const amount = parseFloat(value);
    if (isNaN(amount) || amount <= 0) return 0;
    return amount * 0.05; // 5% processing fee
  };

  // Calculate the total amount (amount + processing fee) for display only
  const calculateTotalAmount = (value: string) => {
    const amount = parseFloat(value);
    if (isNaN(amount) || amount <= 0) return 0;
    return amount + calculateProcessingFee(value);
  };

  // Format currency
  const formatCurrency = (value: string) => {
    const amount = parseFloat(value);
    if (isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (parsedAmount > 0) {
      onSubmit(parsedAmount); // Send only the input amount (without processing fee)
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Amount to Pay (USD)
        </label>
        <input
          id="amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary dark:bg-gray-700 dark:text-white sm:text-sm"
          step="0.01"
          min="0"
        />
      </div>

      {/* Display processing fee and total amount for viewing only */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        <p>Processing Fee: {formatCurrency(calculateProcessingFee(amount).toString())}</p>
        <p className="font-semibold">Total Amount: {formatCurrency(calculateTotalAmount(amount).toString())}</p>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <LoadingButton
          type="submit"
          isProcessing={isProcessing}
          loadingText="Processing..."
          disabled={isProcessing || parseFloat(amount) <= 0}
        >
          Continue to Pay {formatCurrency(calculateTotalAmount(amount).toString())} {/* Show total amount in the button */}
        </LoadingButton>
      </div>
    </form>
  );
}